import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import {
  ServerIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhoneIcon,
  WifiIcon,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface Server {
  id: number;
  name: string;
  url: string;
  client_id: string;
  client_secret: string;
  description?: string;
  is_active: boolean;
  last_ping_at?: string;
  created_at: string;
  services_count?: number;
}

const AdminServersPage: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);

  const [serverData, setServerData] = useState({
    name: '',
    url: '',
    client_id: '',
    client_secret: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/servers');
      // Extract servers from nested API response structure
      const serversData = response.data?.data?.servers || [];
      setServers(serversData);
    } catch (err: any) {
      setError('Erro ao carregar servidores');
      console.error('Servers error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateServer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/servers', serverData);
      setShowCreateModal(false);
      resetForm();
      fetchServers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar servidor');
    }
  };

  const handleEditServer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedServer) return;

    try {
      await api.put(`/admin/servers/${selectedServer.id}`, serverData);
      setShowEditModal(false);
      setSelectedServer(null);
      resetForm();
      fetchServers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar servidor');
    }
  };

  const handleDeleteServer = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este servidor?')) {
      return;
    }

    try {
      await api.delete(`/admin/servers/${id}`);
      fetchServers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir servidor');
    }
  };

  const handleTestConnection = async (id: number) => {
    try {
      await api.post(`/admin/servers/${id}/test`);
      alert('Conexão testada com sucesso!');
    } catch (err: any) {
      alert('Erro ao testar conexão: ' + (err.response?.data?.message || 'Erro desconhecido'));
    }
  };

  const resetForm = () => {
    setServerData({
      name: '',
      url: '',
      client_id: '',
      client_secret: '',
      description: '',
      is_active: true,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (server: Server) => {
    setSelectedServer(server);
    setServerData({
      name: server.name,
      url: server.url,
      client_id: server.client_id,
      client_secret: server.client_secret,
      description: server.description || '',
      is_active: server.is_active,
    });
    setShowEditModal(true);
  };

  const getStatusIcon = (is_active: boolean) => {
    return is_active
      ? <CheckCircleIcon className="h-5 w-5 text-green-500" />
      : <XCircleIcon className="h-5 w-5 text-red-500" />;
  };

  const getStatusColor = (is_active: boolean) => {
    return is_active
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('pt-BR');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Servidores</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure e gerencie os servidores Asterisk
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Novo Servidor
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Servers List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {servers.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Nenhum servidor encontrado
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece adicionando seu primeiro servidor Asterisk.
            </p>
            <div className="mt-6">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Adicionar Servidor
              </button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {servers.map((server) => (
              <li key={server.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ServerIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            {server.name}
                          </p>
                          <div className="ml-2 flex items-center">
                            {getStatusIcon(server.is_active)}
                          </div>
                        </div>
                        <div className="mt-1 flex items-center space-x-4">
                          <p className="text-sm text-gray-500">
                            {server.url}
                          </p>
                          <div className="flex items-center text-sm text-gray-500">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {server.services_count || 0} serviços
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          server.is_active
                        )}`}
                      >
                        {server.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleTestConnection(server.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                          title="Testar Conexão com Asterisk API"
                        >
                          <WifiIcon className="h-3 w-3 mr-1" />
                          Testar
                        </button>
                        <button
                          onClick={() => openEditModal(server)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Editar"
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteServer(server.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Excluir"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-500">
                        {formatDate(server.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create Server Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    Criar Novo Servidor
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Configure um novo servidor Asterisk
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleCreateServer} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome do Servidor
                  </label>
                  <input
                    type="text"
                    required
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={serverData.name}
                    onChange={(e) =>
                      setServerData({ ...serverData, name: e.target.value })
                    }
                    placeholder="Ex: Servidor Principal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    URL da API
                  </label>
                  <input
                    type="url"
                    required
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={serverData.url}
                    onChange={(e) =>
                      setServerData({ ...serverData, url: e.target.value })
                    }
                    placeholder="https://roxy.sip.call.wabby.io/api"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      required
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={serverData.client_id}
                      onChange={(e) =>
                        setServerData({ ...serverData, client_id: e.target.value })
                      }
                      placeholder="client_001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      required
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={serverData.client_secret}
                      onChange={(e) =>
                        setServerData({ ...serverData, client_secret: e.target.value })
                      }
                      placeholder="••••••••••••••••••••"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    rows={3}
                    value={serverData.description}
                    onChange={(e) =>
                      setServerData({ ...serverData, description: e.target.value })
                    }
                    placeholder="Descrição opcional do servidor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={serverData.is_active ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setServerData({
                        ...serverData,
                        is_active: e.target.value === 'active',
                      })
                    }
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all transform hover:scale-105"
                  >
                    Criar Servidor
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Server Modal */}
      {showEditModal && selectedServer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    Editar Servidor
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Atualize as configurações do servidor
                  </p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleEditServer} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome do Servidor
                  </label>
                  <input
                    type="text"
                    required
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={serverData.name}
                    onChange={(e) =>
                      setServerData({ ...serverData, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    URL da API
                  </label>
                  <input
                    type="url"
                    required
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={serverData.url}
                    onChange={(e) =>
                      setServerData({ ...serverData, url: e.target.value })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      required
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={serverData.client_id}
                      onChange={(e) =>
                        setServerData({ ...serverData, client_id: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      required
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={serverData.client_secret}
                      onChange={(e) =>
                        setServerData({ ...serverData, client_secret: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    rows={3}
                    value={serverData.description}
                    onChange={(e) =>
                      setServerData({ ...serverData, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={serverData.is_active ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setServerData({
                        ...serverData,
                        is_active: e.target.value === 'active',
                      })
                    }
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all transform hover:scale-105"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServersPage;